'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { MessageCircle, TrendingUp, Clock } from 'lucide-react';
import { getLiveWorldCupFixtures, getUpcomingFixtures, getStatusLabel, isInPlay, getFlag, type LiveFixture } from '@/lib/api-football';
import { subscribeToScoreUpdates, type ScoreUpdatePayload } from '@/lib/supabase-realtime';
import { useLanguage } from '@/contexts/LanguageContext';

export default function HeroMatchCard() {
  const router = useRouter();
  const [heroMatch, setHeroMatch] = useState<LiveFixture | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { t } = useLanguage();

  const fetchHero = useCallback(async () => {
    try {
      const [liveData, upcomingData] = await Promise.all([
        getLiveWorldCupFixtures(),
        getUpcomingFixtures(5),
      ]);
      const inPlay = (liveData ?? []).filter(m => isInPlay(m.fixture.status));
      const best = inPlay[0] ?? liveData?.[0] ?? upcomingData?.[0] ?? null;
      setHeroMatch(best);
    } catch {
      // leave as null — skeleton shown
    }
  }, []);

  useEffect(() => {
    fetchHero();
    intervalRef.current = setInterval(fetchHero, 15_000);

    const unsub = subscribeToScoreUpdates((payload: ScoreUpdatePayload) => {
      setHeroMatch(prev => {
        if (!prev || prev.fixture.id !== payload.fixtureId) return prev;
        return {
          ...prev,
          goals: { home: payload.homeScore, away: payload.awayScore },
          fixture: {
            ...prev.fixture,
            status: { ...prev.fixture.status, elapsed: payload.minute },
          },
        };
      });
    });

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (unsub) unsub();
    };
  }, [fetchHero]);

  if (!heroMatch) {
    return (
      <div className="rounded-2xl animate-pulse" style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 20%)', height: 280 }} />
    );
  }

  const { fixture, league, teams, goals } = heroMatch;
  const statusLabel = getStatusLabel(fixture.status);
  const inPlay = isInPlay(fixture.status);

  return (
    <div
      className="relative rounded-2xl overflow-hidden card-hover cursor-pointer"
      style={{
        background: 'linear-gradient(135deg, hsl(222 35% 11%) 0%, hsl(222 28% 14%) 100%)',
        border: '1px solid hsl(222 25% 20%)',
      }}
      onClick={() => router.push(`/game-detail?id=${fixture.id}`)}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-64 h-64 rounded-full opacity-10 blur-3xl" style={{ background: 'hsl(25 95% 53%)' }} />
        <div className="absolute bottom-0 right-1/4 w-48 h-48 rounded-full opacity-8 blur-3xl" style={{ background: 'hsl(189 94% 51%)' }} />
      </div>

      <div className="relative p-6 lg:p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            {inPlay ? (
              <span className="flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: 'hsl(0 84% 60% / 0.15)', color: 'hsl(0 84% 70%)' }}>
                <span className="w-1.5 h-1.5 rounded-full bg-red-400 live-pulse" />
                LIVE
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: 'hsl(38 92% 50% / 0.15)', color: 'hsl(38 92% 65%)' }}>
                {fixture.status.short}
              </span>
            )}
            <span className="text-sm font-medium text-slate-400">{league.round} · {fixture.venue.name ?? ''}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5 text-sm font-bold text-cyan-400">
              <Clock size={14} />
              <span className="font-mono tabular-nums">{statusLabel}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 flex flex-col items-center gap-2">
            {teams.home.logo ? (
              <img src={teams.home.logo} alt={teams.home.name} className="w-14 h-14 object-contain" />
            ) : (
              <span className="text-5xl">{getFlag(teams.home.name)}</span>
            )}
            <span className="text-lg font-bold text-white">{teams.home.name}</span>
            <span className="text-xs text-slate-500 font-medium">{teams.home.code ?? teams.home.name.slice(0, 3).toUpperCase()}</span>
          </div>

          <div className="flex flex-col items-center gap-1">
            <div className="flex items-center gap-3">
              <span className="font-mono text-6xl font-black text-white tabular-nums">{goals.home ?? 0}</span>
              <span className="text-3xl font-light text-slate-600">–</span>
              <span className="font-mono text-6xl font-black text-white tabular-nums">{goals.away ?? 0}</span>
            </div>
            <span className="text-xs text-slate-500 tracking-widest uppercase">{fixture.status.long}</span>
          </div>

          <div className="flex-1 flex flex-col items-center gap-2">
            {teams.away.logo ? (
              <img src={teams.away.logo} alt={teams.away.name} className="w-14 h-14 object-contain" />
            ) : (
              <span className="text-5xl">{getFlag(teams.away.name)}</span>
            )}
            <span className="text-lg font-bold text-white">{teams.away.name}</span>
            <span className="text-xs text-slate-500 font-medium">{teams.away.code ?? teams.away.name.slice(0, 3).toUpperCase()}</span>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between pt-4" style={{ borderTop: '1px solid hsl(222 25% 18%)' }}>
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <span>{fixture.venue.city ?? ''}</span>
          </div>
          <div className="flex items-center gap-2">
            <Link
              href="/live-chat"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1.5 text-sm font-medium text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              <MessageCircle size={15} />
              <span>{t.chat.title}</span>
            </Link>
            <span className="text-slate-600 mx-1">·</span>
            <span className="flex items-center gap-1.5 text-sm text-slate-400">
              <TrendingUp size={14} className="text-orange-400" />
              <span>{t.dashboard.featured}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}